import { isInvalidSessionError, isPublicKeyRefused } from '@/services/staffSession';
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, formatDistanceToNowStrict, parseISO } from 'date-fns';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BellRing,
  BriefcaseMedical,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  ClipboardList,
  Download,
  ExternalLink,
  Filter,
  Layers3,
  MessageSquareMore,
  Plus,
  RefreshCw,
  Search,
  UserRound,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useClinic } from '@/contexts/ClinicContext';
import { cn } from '@/lib/utils';
import { datedFilename, exportRowsToCsv } from '@/lib/exportCsv';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import PatientVisitHistory from '@/components/PatientVisitHistory';
import PatientCaseModal, {
  PatientCaseFormValues,
  PatientCaseOwnerOption,
  PatientCaseTreatmentRequest,
  PatientCaseUiRecord,
  PatientCaseUiStatus,
} from '@/components/PatientCaseModal';
import { getStaff } from '@/services/adminService';
import { getPatientDoctorOwners } from '@/lib/patientOwners';
import {
  getReferralConversionStatus,
  normalizePatientCaseStatus,
  PatientCaseActivityWithDetails,
  PatientCaseInsert,
  PatientCaseUpdate,
  PatientCaseWithDetails,
} from '@/services/patientCaseService';
import {
  useCaseActivities,
  usePatientCase,
  usePatientCaseMutations,
  usePatientCases,
} from '@/hooks/usePatientCases';
import { useToast } from '@/hooks/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import ReferringDentistsTable from '@/components/ReferringDentistsTable';
import ReferralCaselist from '@/components/ReferralCaselist';
import ReferralReconciliation from '@/components/ReferralReconciliation';
import { formatLeadSource, leadSourceBadgeClass, DEFAULT_MANUAL_LEAD_SOURCE } from '@/lib/leadSource';
import { FinishingSoonBar, FinishingSoonRowNote } from '@/components/FinishingSoonBar';
import { useFinishingSoon } from '@/hooks/useFinishingSoon';
import { doctorKey as finishingSoonDoctorKey } from '@/services/finishingSoonService';

interface PatientCaseActivityPreview {
  id: string;
  caseId: string;
  activityType: 'status_change' | 'communication' | 'ownership_change' | 'note' | 'referred_back';
  title: string;
  detail: string;
  actor: string;
  createdAt: string;
}

interface ReferralAttachmentLink {
  id: string;
  fileName: string;
  mimeType: string;
  byteSize: number;
  uploadedAt: string | null;
  signedUrl: string;
}

const PATIENT_CASES_STORAGE_KEY = 'patients-preview-cases-v1';
const PATIENT_CASE_ACTIVITIES_STORAGE_KEY = 'patients-preview-activities-v1';
const BRAND_NAME = 'Twin City Endodontics';
const LEGACY_BRAND_NAME = 'Twin City Dental';

const normalizeBrandText = (value: string | null | undefined) =>
  (value ?? '').replaceAll(LEGACY_BRAND_NAME, BRAND_NAME);

const STATUS_META: Record<
  PatientCaseUiStatus,
  { label: string; badgeClassName: string; columnLabel: string }
> = {
  new_referral: {
    label: 'New Referral',
    badgeClassName: 'border-amber-300 bg-amber-50 text-amber-900',
    columnLabel: 'New',
  },
  appointment_scheduled: {
    label: 'Appointment Scheduled',
    badgeClassName: 'border-blue-300 bg-blue-50 text-blue-900',
    columnLabel: 'Scheduled',
  },
  patient_seen: {
    label: 'Patient Seen',
    badgeClassName: 'border-cyan-300 bg-cyan-50 text-cyan-900',
    columnLabel: 'Seen',
  },
  treatment_proposed: {
    label: 'Treatment Proposed',
    badgeClassName: 'border-indigo-300 bg-indigo-50 text-indigo-900',
    columnLabel: 'Proposed',
  },
  treatment_accepted: {
    label: 'Treatment Accepted',
    badgeClassName: 'border-emerald-300 bg-emerald-50 text-emerald-900',
    columnLabel: 'Accepted',
  },
  treatment_ongoing: {
    label: 'Ongoing Treatment',
    badgeClassName: 'border-sky-300 bg-sky-50 text-sky-900',
    columnLabel: 'Ongoing',
  },
  referred_to_other_specialty: {
    label: 'Referred to Other Specialty',
    badgeClassName: 'border-purple-300 bg-purple-50 text-purple-900',
    columnLabel: 'Other specialty',
  },
  no_show: {
    label: 'No Show',
    badgeClassName: 'border-orange-300 bg-orange-50 text-orange-900',
    columnLabel: 'No show',
  },
  non_compliant: {
    label: 'Non-compliant / Not Returning',
    badgeClassName: 'border-red-300 bg-red-50 text-red-900',
    columnLabel: 'Non-compliant',
  },
  referred_back: {
    label: 'Referred Back',
    badgeClassName: 'border-violet-300 bg-violet-50 text-violet-900',
    columnLabel: 'Referred back',
  },
  patient_did_not_proceed: {
    label: 'Patient Did Not Proceed',
    badgeClassName: 'border-rose-300 bg-rose-50 text-rose-900',
    columnLabel: 'Did not proceed',
  },
  lost_inactive: {
    label: 'Lost / Inactive',
    badgeClassName: 'border-slate-300 bg-slate-100 text-slate-700',
    columnLabel: 'Inactive',
  },
};

// Treatment-progress grouping (Dr Ang 2026-06-11, ts 02:50): "patient seen",
// "waiting for treatment" and "ongoing treatment" are ONE control, not three
// separate buttons/columns. This is presentation only — the underlying enum
// keeps all its values. "Waiting for treatment" has no distinct enum value, so
// it maps to `treatment_accepted` (patient seen + treatment accepted, awaiting
// the treatment start). The standalone "Patient Seen" label is hidden in favour
// of the grouped label "In treatment" wherever a single badge is shown.
const PROGRESS_STATUSES: PatientCaseUiStatus[] = [
  'patient_seen',
  'treatment_accepted',
  'treatment_ongoing',
];

const PROGRESS_SEGMENT_LABELS: Record<string, string> = {
  patient_seen: 'Seen',
  treatment_accepted: 'Waiting for treatment',
  treatment_ongoing: 'Ongoing treatment',
};

// Single label shown when a progress-band case appears as one badge (board /
// list / detail). Replaces the standalone "Patient Seen" / "Accepted" / etc.
const PROGRESS_GROUP_LABEL = 'In treatment';

const isProgressStatus = (status: PatientCaseUiStatus | string): boolean =>
  (PROGRESS_STATUSES as string[]).includes(status);

// Single-badge label: progress statuses collapse to one "In treatment" label so
// "Patient Seen" never appears as its own standalone label on the board/list.
const getStatusBadgeLabel = (status: PatientCaseUiStatus): string =>
  isProgressStatus(status) ? PROGRESS_GROUP_LABEL : STATUS_META[status].label;

const getStatusColumnLabel = (status: PatientCaseUiStatus): string =>
  isProgressStatus(status) ? PROGRESS_GROUP_LABEL : STATUS_META[status].columnLabel;

// ONE ladder for the case-detail stepper (flow fix 2026-07-03): the main funnel
// in order. Off-ramps render separately below it. Labels stay short so the
// stepper reads at a glance.
const MAIN_PIPELINE_STATUSES: PatientCaseUiStatus[] = [
  'new_referral',
  'appointment_scheduled',
  'patient_seen',
  'treatment_proposed',
  'treatment_accepted',
  'treatment_ongoing',
  'referred_back',
];

const MAIN_PIPELINE_LABELS: Partial<Record<PatientCaseUiStatus, string>> = {
  new_referral: 'New',
  appointment_scheduled: 'Scheduled',
  patient_seen: 'Seen',
  treatment_proposed: 'Proposed',
  treatment_accepted: 'Waiting for treatment',
  treatment_ongoing: 'Ongoing',
  referred_back: 'Referred back',
};

// Test/demo cases are hidden from live views (still findable via search).
const TEST_CASE_RE = /\b(test|testing|dummy|demo|zzztest|please ignore)\b/i;
const isTestCase = (patientCase: {
  patientDisplayName?: string | null;
  platoReferenceId?: string | null;
}): boolean =>
  TEST_CASE_RE.test(patientCase.patientDisplayName ?? '') ||
  TEST_CASE_RE.test(patientCase.platoReferenceId ?? '');

// Referred back is the last stage of the main pipeline above (Completed and
// Referred Back are one status), so it is not repeated here.
const OFF_RAMP_STATUSES: PatientCaseUiStatus[] = [
  'referred_to_other_specialty',
  'no_show',
  'patient_did_not_proceed',
  'non_compliant',
  'lost_inactive',
];

const ACTIVE_REFERRAL_STATUSES: PatientCaseUiStatus[] = [
  'new_referral',
  'appointment_scheduled',
  'patient_seen',
  'treatment_proposed',
  'treatment_accepted',
  'treatment_ongoing',
  'referred_to_other_specialty',
  'no_show',
  'referred_back',
];

// Operations board priority bands. Cases needing immediate attention pin to the
// top; in-progress treatment sits in the middle; closed / inactive collapses to
// the bottom so the dense action list dominates the viewport.
const ACTION_NEEDED_STATUSES: PatientCaseUiStatus[] = [
  'new_referral',
  'appointment_scheduled',
  'patient_seen',
  'treatment_proposed',
  'no_show',
];

const IN_PROGRESS_STATUSES: PatientCaseUiStatus[] = [
  'treatment_accepted',
  'treatment_ongoing',
  'referred_to_other_specialty',
];

const CLOSED_STATUSES: PatientCaseUiStatus[] = [
  'referred_back',
  'patient_did_not_proceed',
  'non_compliant',
  'lost_inactive',
];

const getStatusBand = (
  status: PatientCaseUiStatus
): 'action' | 'progress' | 'closed' => {
  if (ACTION_NEEDED_STATUSES.includes(status)) {
    return 'action';
  }
  if (IN_PROGRESS_STATUSES.includes(status)) {
    return 'progress';
  }
  return 'closed';
};

const isReferralLoopCompleteStatus = (status: PatientCaseUiStatus | string) =>
  status === 'referred_back';

const getReferralLoopLabel = (patientCase: Pick<PatientCaseUiRecord, 'status'>) => {
  if (isReferralLoopCompleteStatus(patientCase.status)) {
    return 'Referred back to clinic';
  }

  if (['no_show', 'patient_did_not_proceed', 'lost_inactive', 'non_compliant'].includes(patientCase.status)) {
    return 'NIL';
  }

  return 'Ongoing treatment';
};

const CONVERSION_META = {
  pending: {
    label: 'Pending',
    badgeClassName: 'border-amber-300 bg-amber-50 text-amber-900',
  },
  converted: {
    label: 'Converted',
    badgeClassName: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  },
  not_converted: {
    label: 'Not Converted',
    badgeClassName: 'border-rose-300 bg-rose-50 text-rose-900',
  },
  inactive: {
    label: 'Inactive',
    badgeClassName: 'border-slate-300 bg-slate-100 text-slate-700',
  },
} as const;

const buildInitialOwners = (clinicName: string): PatientCaseOwnerOption[] => [
  { id: 'owner-joanne', name: 'Joanne Wong', role: `${clinicName} Operations` },
  { id: 'owner-marcus', name: 'Marcus Lim', role: 'Referral Coordinator' },
  { id: 'owner-siti', name: 'Siti Rahman', role: 'Patient Liaison' },
  { id: 'owner-ashley', name: 'Ashley Tay', role: 'Case Follow-up' },
];

const buildInitialCases = (
  primaryClinicName: string,
  primaryClinicId: string,
  secondaryClinicName: string,
  secondaryClinicId: string
): PatientCaseUiRecord[] => [
  {
    id: 'case-preview-001',
    patientDisplayName: 'Jeremy Tan',
    platoReferenceId: 'PL-20481',
    referringDoctor: 'Dr Koh',
    referringClinic: 'Forum Dental',
    referralChannel: 'email',
    referralReceivedDate: '2026-04-07',
    clinicId: primaryClinicId,
    clinicName: primaryClinicName,
    status: 'new_referral',
    conversionStatus: 'pending',
    conversionDate: null,
    nextAction: 'Confirm consultation slot and gather x-rays',
    nextActionDueDate: '2026-04-10',
    assignedOwnerStaffId: 'owner-joanne',
    assignedOwnerName: 'Joanne Wong',
    referredBackFlag: false,
    referredBackDate: null,
    remarks: 'High-value referral. Wants afternoon slot and quick turnaround.',
    externalStatusNote: 'Referral received. The team is reviewing the case.',
    statusChangedAt: '2026-04-08T10:30:00+08:00',
    treatmentAcceptedAt: null,
    completedAt: null,
    inactiveAt: null,
    createdAt: '2026-04-07T09:15:00+08:00',
    updatedAt: '2026-04-08T10:30:00+08:00',
  },
  {
    id: 'case-preview-002',
    patientDisplayName: 'Alicia Neo',
    platoReferenceId: 'PL-20492',
    referringDoctor: 'Dr Yap',
    referringClinic: 'Smile Studio',
    referralChannel: 'phone',
    referralReceivedDate: '2026-04-04',
    clinicId: primaryClinicId,
    clinicName: primaryClinicName,
    status: 'treatment_ongoing',
    conversionStatus: 'converted',
    conversionDate: '2026-04-05',
    nextAction: 'Chase lab ETA and update patient by Friday',
    nextActionDueDate: '2026-04-11',
    assignedOwnerStaffId: 'owner-marcus',
    assignedOwnerName: 'Marcus Lim',
    referredBackFlag: false,
    referredBackDate: null,
    remarks: 'Patient prefers WhatsApp updates once impressions are ready.',
    externalStatusNote: 'Treatment is ongoing. The clinic will update when the next milestone is ready.',
    statusChangedAt: '2026-04-05T12:10:00+08:00',
    treatmentAcceptedAt: '2026-04-05T12:10:00+08:00',
    completedAt: null,
    inactiveAt: null,
    createdAt: '2026-04-04T13:20:00+08:00',
    updatedAt: '2026-04-08T16:15:00+08:00',
  },
  {
    id: 'case-preview-003',
    patientDisplayName: 'Nur Izzah',
    platoReferenceId: 'PL-20504',
    referringDoctor: 'Dr Ong',
    referringClinic: secondaryClinicName,
    referralChannel: 'online',
    referralReceivedDate: '2026-04-02',
    clinicId: secondaryClinicId,
    clinicName: secondaryClinicName,
    status: 'referred_back',
    conversionStatus: 'converted',
    conversionDate: '2026-04-08',
    nextAction: 'Archive handback notes and close loop',
    nextActionDueDate: '2026-04-12',
    assignedOwnerStaffId: 'owner-siti',
    assignedOwnerName: 'Siti Rahman',
    referredBackFlag: true,
    referredBackDate: '2026-04-08',
    remarks: 'Referring doctor requested a concise summary for their records.',
    externalStatusNote: 'Case has been referred back to the originating clinic.',
    statusChangedAt: '2026-04-08T18:05:00+08:00',
    treatmentAcceptedAt: '2026-04-08T18:05:00+08:00',
    completedAt: null,
    inactiveAt: null,
    createdAt: '2026-04-02T11:00:00+08:00',
    updatedAt: '2026-04-08T18:05:00+08:00',
  },
  {
    id: 'case-preview-004',
    patientDisplayName: 'Benjamin Chua',
    platoReferenceId: 'PL-20518',
    referringDoctor: 'Dr Lim',
    referringClinic: primaryClinicName,
    referralChannel: 'walk-in',
    referralReceivedDate: '2026-03-30',
    clinicId: primaryClinicId,
    clinicName: primaryClinicName,
    status: 'referred_back',
    conversionStatus: 'converted',
    conversionDate: '2026-04-06',
    nextAction: 'Send appreciation note to referrer',
    nextActionDueDate: '2026-04-14',
    assignedOwnerStaffId: 'owner-ashley',
    assignedOwnerName: 'Ashley Tay',
    referredBackFlag: false,
    referredBackDate: null,
    remarks: 'Good candidate for referral loyalty follow-up.',
    externalStatusNote: 'Case completed.',
    statusChangedAt: '2026-04-06T09:50:00+08:00',
    treatmentAcceptedAt: '2026-04-03T09:10:00+08:00',
    completedAt: '2026-04-06T09:50:00+08:00',
    inactiveAt: null,
    createdAt: '2026-03-30T15:45:00+08:00',
    updatedAt: '2026-04-06T09:50:00+08:00',
  },
];

const buildInitialActivities = (): PatientCaseActivityPreview[] => [
  {
    id: 'activity-preview-001',
    caseId: 'case-preview-001',
    activityType: 'communication',
    title: 'Referral package received',
    detail: 'Received referral documents and preliminary radiographs by email.',
    actor: 'Joanne Wong',
    createdAt: '2026-04-07T09:30:00+08:00',
  },
  {
    id: 'activity-preview-002',
    caseId: 'case-preview-001',
    activityType: 'note',
    title: 'Patient availability noted',
    detail: 'Best slot window is weekday afternoons after 3pm.',
    actor: 'Joanne Wong',
    createdAt: '2026-04-08T10:30:00+08:00',
  },
  {
    id: 'activity-preview-003',
    caseId: 'case-preview-002',
    activityType: 'status_change',
    title: 'Case moved to ongoing treatment',
    detail: 'Doctor accepted referral and treatment planning is in progress.',
    actor: 'Marcus Lim',
    createdAt: '2026-04-05T12:10:00+08:00',
  },
  {
    id: 'activity-preview-004',
    caseId: 'case-preview-002',
    activityType: 'communication',
    title: 'Patient contacted',
    detail: 'Updated patient on treatment timeline and next contact point.',
    actor: 'Marcus Lim',
    createdAt: '2026-04-08T16:15:00+08:00',
  },
  {
    id: 'activity-preview-005',
    caseId: 'case-preview-003',
    activityType: 'referred_back',
    title: 'Referred back to originating clinic',
    detail: 'Loop completed and handback summary sent to referrer.',
    actor: 'Siti Rahman',
    createdAt: '2026-04-08T18:05:00+08:00',
  },
  {
    id: 'activity-preview-006',
    caseId: 'case-preview-004',
    activityType: 'ownership_change',
    title: 'Ownership updated',
    detail: 'Ashley Tay is now responsible for post-treatment follow-up.',
    actor: 'Clinic Admin',
    createdAt: '2026-04-03T09:10:00+08:00',
  },
];

const parseStoredValue = <T,>(value: string | null): T | null => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as T;
  } catch (error) {
    console.warn('Failed to parse preview storage value:', error);
    return null;
  }
};

const formatDisplayDate = (value: string | null, fallback = 'Not set') => {
  if (!value) {
    return fallback;
  }

  return format(parseISO(value), 'dd MMM yyyy');
};

const getDaysUntilDue = (value: string | null) => {
  if (!value) {
    return null;
  }

  return formatDistanceToNowStrict(parseISO(value), { addSuffix: true });
};

const formatFileSize = (byteSize: number) => {
  if (byteSize < 1024 * 1024) {
    return `${Math.max(1, Math.round(byteSize / 1024))} KB`;
  }

  return `${(byteSize / (1024 * 1024)).toFixed(1)} MB`;
};

const parseTreatmentRequests = (value: unknown): PatientCaseTreatmentRequest[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === 'object')
    .map((item) => ({
      key: typeof item.key === 'string' ? item.key : String(item.label ?? ''),
      label: typeof item.label === 'string' ? item.label : 'Treatment request',
      selected: item.selected === true,
      toothNumber: typeof item.toothNumber === 'string' ? item.toothNumber : '',
    }))
    .filter((item) => item.label.trim().length > 0);
};

const getActivityIcon = (activityType: PatientCaseActivityPreview['activityType']) => {
  switch (activityType) {
    case 'communication':
      return MessageSquareMore;
    case 'ownership_change':
      return UserRound;
    case 'referred_back':
      return CheckCircle2;
    case 'status_change':
      return Layers3;
    case 'note':
    default:
      return ClipboardList;
  }
};

const isPatientCaseSchemaUnavailable = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const candidate = error as { code?: string; message?: string; details?: string };
  const combinedMessage = `${candidate.message ?? ''} ${candidate.details ?? ''}`.toLowerCase();

  return (
    candidate.code === '42P01' ||
    candidate.code === 'PGRST200' ||
    combinedMessage.includes('patient_cases') ||
    combinedMessage.includes('patient_case_activities') ||
    combinedMessage.includes('relation') && combinedMessage.includes('does not exist')
  );
};

const isPatientCaseNotFound = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const candidate = error as { code?: string; message?: string; details?: string };
  const combinedMessage = `${candidate.message ?? ''} ${candidate.details ?? ''}`.toLowerCase();

  return candidate.code === 'PGRST116' || combinedMessage.includes('0 rows');
};

// Manually logged referrals (WhatsApp / phone, no Plato record yet) carry a
// portal-style PENDING- placeholder reference and channel 'manual'. Surface a
// badge so staff can spot cases that still need a real Plato ID.
const isManualReferralCase = (patientCase: PatientCaseUiRecord): boolean =>
  patientCase.referralChannel === 'manual' &&
  (patientCase.platoReferenceId ?? '').startsWith('PENDING-');

const ManualReferralBadge = ({ compact = false }: { compact?: boolean }) => (
  <Badge
    variant="outline"
    className={cn(
      'border-amber-300 bg-amber-50 text-amber-800',
      compact && 'whitespace-nowrap px-1.5 py-0 text-[10px] font-medium'
    )}
  >
    Manual
  </Badge>
);

const mapPatientCaseToUiRecord = (
  patientCase: PatientCaseWithDetails,
  fallbackClinicName: string
): PatientCaseUiRecord => {
  const status = normalizePatientCaseStatus(patientCase.status);
  // referred_back_flag is now an independent column: a case can be marked as
  // referred back even if the workflow status is something else.
  const referredBackFlag = Boolean(patientCase.referred_back_flag);

  return {
    id: patientCase.id,
    patientDisplayName: patientCase.patient_display_name,
    patientNric: patientCase.patient_nric,
    patientPhone: patientCase.patient_phone,
    platoReferenceId: patientCase.plato_reference_id,
    platoPatientNumber: patientCase.plato_patient_number ?? null,
    clinicEmail: patientCase.clinic_email,
    clinicBranch: patientCase.clinic_branch,
    preferredSpecialist: patientCase.preferred_specialist,
    treatmentRequests: parseTreatmentRequests(patientCase.treatment_requests),
    referralFormVersion: patientCase.referral_form_version,
    submissionNote: patientCase.submission_note,
    referringDoctor: patientCase.referring_doctor ?? '',
    referringClinic: patientCase.referring_clinic ?? '',
    referralChannel: patientCase.referral_channel ?? 'other',
    source: (patientCase as { source?: string | null }).source ?? null,
    referralReceivedDate: patientCase.referral_received_date,
    clinicId: patientCase.clinic_id,
    clinicName: patientCase.clinic?.name ?? fallbackClinicName,
    status,
    conversionStatus: patientCase.conversion_status ?? getReferralConversionStatus(status),
    conversionDate: patientCase.conversion_date,
    nextAction: patientCase.next_action ?? '',
    nextActionDueDate: patientCase.next_action_due_date,
    assignedOwnerStaffId: patientCase.assigned_owner_staff_id,
    assignedOwnerName: patientCase.assigned_owner?.name ?? null,
    referredBackFlag,
    referredBackDate: referredBackFlag ? patientCase.referred_back_date : null,
    remarks: patientCase.remarks ?? '',
    externalStatusNote: normalizeBrandText(patientCase.external_status_note),
    statusChangedAt: patientCase.status_changed_at ?? patientCase.updated_at,
    treatmentAcceptedAt: patientCase.treatment_accepted_at,
    completedAt: patientCase.completed_at,
    inactiveAt: patientCase.inactive_at,
    createdAt: patientCase.created_at,
    updatedAt: patientCase.updated_at,
    // Waitlist fields — columns pending migration, accessed via `as any`
    ...(patientCase as any).plato_waitlist_pushed_at
      ? {
          platoWaitlistPushedAt: (patientCase as any).plato_waitlist_pushed_at,
          platoWaitlistDate: (patientCase as any).plato_waitlist_date,
          platoWaitlistLocation: (patientCase as any).plato_waitlist_location,
        }
      : {},
  };
};

const normalizePreviewCase = (patientCase: PatientCaseUiRecord): PatientCaseUiRecord => {
  const status = normalizePatientCaseStatus(patientCase.status);
  // referred_back_flag is now independent of status. Default to the value on
  // the seed record; if missing, fall back to status-derived for legacy
  // localStorage snapshots so existing previews don't lose their pill.
  const referredBackFlag =
    typeof patientCase.referredBackFlag === 'boolean'
      ? patientCase.referredBackFlag
      : isReferralLoopCompleteStatus(status);
  return {
    ...patientCase,
    status,
    conversionStatus: patientCase.conversionStatus ?? getReferralConversionStatus(status),
    conversionDate: patientCase.conversionDate ?? null,
    referredBackFlag,
    referredBackDate: referredBackFlag ? patientCase.referredBackDate ?? null : null,
    externalStatusNote: normalizeBrandText(patientCase.externalStatusNote),
    statusChangedAt: patientCase.statusChangedAt ?? patientCase.updatedAt,
    patientNric: patientCase.patientNric ?? null,
    patientPhone: patientCase.patientPhone ?? null,
    clinicEmail: patientCase.clinicEmail ?? null,
    clinicBranch: patientCase.clinicBranch ?? null,
    preferredSpecialist: patientCase.preferredSpecialist ?? null,
    treatmentRequests: patientCase.treatmentRequests ?? [],
    referralFormVersion: patientCase.referralFormVersion ?? null,
    submissionNote: patientCase.submissionNote ?? null,
    treatmentAcceptedAt: patientCase.treatmentAcceptedAt ?? null,
    completedAt: patientCase.completedAt ?? null,
    inactiveAt: patientCase.inactiveAt ?? null,
  };
};

const mapActivityToPreview = (
  activity: PatientCaseActivityWithDetails
): PatientCaseActivityPreview => {
  const getTitle = () => {
    switch (activity.activity_type) {
      case 'status_change':
        return 'Status updated';
      case 'ownership_change':
        return 'Ownership changed';
      case 'referred_back':
        return 'Referred-back update';
      case 'communication':
        return 'Communication milestone';
      case 'note':
      default:
        return 'Operational note';
    }
  };

  const getDetail = () => {
    if (activity.note?.trim()) {
      return activity.note.trim();
    }

    if (activity.from_value || activity.to_value) {
      const fromValue = activity.from_value ?? 'Unknown';
      const toValue = activity.to_value ?? 'Unknown';
      return `${fromValue} -> ${toValue}`;
    }

    return 'Timeline updated.';
  };

  return {
    id: activity.id,
    caseId: activity.case_id,
    activityType: activity.activity_type,
    title: getTitle(),
    detail: getDetail(),
    actor: activity.created_by_user?.name ?? (activity.created_by ? 'Clinic user' : 'Automatic (Plato)'),
    createdAt: activity.created_at,
  };
};

interface ReferredBackIndicatorProps {
  referredBackFlag: boolean;
  referredBackDate: string | null;
  status: PatientCaseUiStatus;
  disabled?: boolean;
  onToggle: (nextFlag: boolean) => void;
}

const ReferredBackIndicator: React.FC<ReferredBackIndicatorProps> = ({
  referredBackFlag,
  referredBackDate,
  status,
  disabled,
  onToggle,
}) => {
  if (referredBackFlag) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          onToggle(false);
        }}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-900 transition hover:border-emerald-400 hover:bg-emerald-100',
          disabled && 'opacity-60'
        )}
        title="Click to clear the referred-back flag"
        aria-label={`Referred back${referredBackDate ? ' on ' + referredBackDate : ''}. Click to clear.`}
      >
        <CheckCircle2 className="h-3.5 w-3.5" />
        <span>
          Referred back
          {referredBackDate ? ` · ${format(parseISO(referredBackDate), 'dd MMM')}` : ''}
        </span>
      </button>
    );
  }

  // Referred Back is also what Completed now means, so a case at this status
  // whose referring clinic has not been marked yet gets the amber prompt.
  const isCompletedStatus = status === 'referred_back';

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onToggle(true);
      }}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-dashed px-2.5 py-1 text-xs font-medium transition',
        isCompletedStatus
          ? 'border-amber-300 bg-amber-50/60 text-amber-800 hover:border-amber-400 hover:bg-amber-50'
          : 'border-slate-300 bg-white text-slate-500 hover:border-slate-400 hover:bg-slate-50',
        disabled && 'opacity-60'
      )}
      title={
        isCompletedStatus
          ? 'Case is completed but not yet marked as referred back. Click to mark.'
          : 'Mark as referred back to the referring clinic'
      }
      aria-label="Mark as referred back"
    >
      <Circle className="h-3.5 w-3.5" />
      <span className="whitespace-nowrap">{isCompletedStatus ? 'Not yet' : 'Mark referred back'}</span>
    </button>
  );
};

interface PatientsViewProps {
  /**
   * Selects which top-level Patients tab is active on mount. Falls back to
   * `list` when not provided or unrecognized. Driven by routes like
   * `/patients/referring-dentists` via SchedulerContent.
   */
  initialTab?: string;
}

// Sub-route slugs that are NOT patient case IDs. When the URL `:id` segment
// matches one of these, PatientsView treats the request as a tab switch
// (e.g. `/patients/referring-dentists` activates the Referring Dentists tab).
const PATIENT_SUB_ROUTE_SLUGS = new Set(['referring-dentists']);

const TAB_ROUTE_SLUGS: Record<string, string> = {
  'referring-dentists': 'referring-dentists',
};

const PatientsView: React.FC<PatientsViewProps> = ({ initialTab }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id: routeParamId } = useParams<{ id?: string }>();
  const location = useLocation();
  // Tab sub-routes (e.g. /patients/referring-dentists) are declared in App.tsx
  // as STATIC routes — they match before /patients/:id, so `useParams().id` is
  // undefined there. Recover the slug from the pathname so deep links still
  // activate the right tab.
  const pathSlug = location.pathname.split('/').filter(Boolean)[1];
  const routeIdOrSlug =
    routeParamId ??
    (pathSlug && PATIENT_SUB_ROUTE_SLUGS.has(pathSlug) ? pathSlug : undefined);
  // If the `:id` segment is one of the known tab slugs, treat it as a tab
  // selector instead of a patient id. Otherwise pass it through as `id`.
  const id =
    routeIdOrSlug && PATIENT_SUB_ROUTE_SLUGS.has(routeIdOrSlug) ? undefined : routeIdOrSlug;
  const tabFromRoute =
    routeIdOrSlug && PATIENT_SUB_ROUTE_SLUGS.has(routeIdOrSlug)
      ? TAB_ROUTE_SLUGS[routeIdOrSlug]
      : undefined;
  const resolvedInitialTab = tabFromRoute ?? initialTab;
  const { user } = useAuth();
  const { currentClinic, clinics } = useClinic();

  const currentClinicName = currentClinic?.name ?? 'Main Clinic';
  const currentClinicId = currentClinic?.id ?? 'clinic-preview-main';
  const alternateClinic = clinics.find((clinic) => clinic.id !== currentClinicId) ?? null;
  const alternateClinicName = alternateClinic?.name ?? 'Ngee Ann City';
  const alternateClinicId = alternateClinic?.id ?? 'clinic-preview-secondary';

  const VALID_TOP_TABS = useMemo(
    () => new Set(['list', 'board', 'analytics', 'reconciliation', 'referring-dentists', 'referral-caselist']),
    [],
  );
  const [activeTopTab, setActiveTopTab] = useState<string>(() =>
    resolvedInitialTab && VALID_TOP_TABS.has(resolvedInitialTab)
      ? resolvedInitialTab
      : 'list',
  );

  // Sync the active tab when the route changes (e.g. user navigates from
  // `/patients` to `/patients/referring-dentists` via the sidebar / back/forward).
  useEffect(() => {
    if (resolvedInitialTab && VALID_TOP_TABS.has(resolvedInitialTab)) {
      setActiveTopTab(resolvedInitialTab);
    }
  }, [VALID_TOP_TABS, resolvedInitialTab]);

  const handleTopTabChange = (next: string) => {
    setActiveTopTab(next);
    // Keep the URL in sync only for tabs that have a dedicated sub-route.
    // The default `list` tab stays at `/patients` so the existing case-detail
    // route `/patients/:id` keeps working as before.
    if (next === 'referring-dentists') {
      navigate('/patients/referring-dentists');
    } else if (
      // Only navigate back to /patients when we are currently sitting on a
      // sub-route slug. Avoid clobbering a case-detail URL when the user
      // simply clicks a different tab while viewing a case.
      routeIdOrSlug &&
      PATIENT_SUB_ROUTE_SLUGS.has(routeIdOrSlug) &&
      next !== 'referring-dentists'
    ) {
      navigate('/patients');
    }
  };

  const [cases, setCases] = useState<PatientCaseUiRecord[]>([]);
  const [activities, setActivities] = useState<PatientCaseActivityPreview[]>([]);
  const previewOwners = useMemo(() => buildInitialOwners(currentClinicName), [currentClinicName]);
  const [hasLoadedPreview, setHasLoadedPreview] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [conversionFilter, setConversionFilter] = useState<string>('all');
  const [ownerFilter, setOwnerFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [showCompleted, setShowCompleted] = useState<'active' | 'all'>('active');
  const [showClosedBoardBand, setShowClosedBoardBand] = useState(false);
  // Detail view: keep the primary next-step (treatment progress + key buttons)
  // front and centre; secondary status options and the long reference cards are
  // progressively disclosed so the case opens compact. Presentation only.
  const [showMoreStatuses, setShowMoreStatuses] = useState(false);
  const [showCaseReference, setShowCaseReference] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCase, setEditingCase] = useState<PatientCaseUiRecord | null>(null);
  const [isSyncingReferrals, setIsSyncingReferrals] = useState(false);
  const patientCasesQuery = usePatientCases({
    clinicId: currentClinic?.id,
    includeClosed: showCompleted === 'all',
  });
  const patientCaseQuery = usePatientCase(id);
  const caseActivitiesQuery = useCaseActivities(id);
  // Finishing soon: cases the daily automatic-close job will close unless kept open (read-only here).
  const finishingSoon = useFinishingSoon();
  const [finishingSoonOnly, setFinishingSoonOnly] = useState(false);
  const [finishingSoonDoctor, setFinishingSoonDoctor] = useState<string | null>(null);
  const ownersQuery = useQuery({
    queryKey: ['patientCaseOwners', currentClinic?.id],
    queryFn: () => getStaff(currentClinic?.id),
    enabled: Boolean(currentClinic?.id),
  });
  const {
    createPatientCase: createLivePatientCase,
    updatePatientCase: updateLivePatientCase,
    deletePatientCase: deleteLivePatientCase,
    setReferredBackFlag: setReferredBackFlagLive,
    isCreating,
    isUpdating,
    isDeleting,
    isFlippingReferredBack,
  } = usePatientCaseMutations();

  useEffect(() => {
    if (hasLoadedPreview) {
      return;
    }

    const storedCases = parseStoredValue<PatientCaseUiRecord[]>(
      window.localStorage.getItem(PATIENT_CASES_STORAGE_KEY)
    );
    const storedActivities = parseStoredValue<PatientCaseActivityPreview[]>(
      window.localStorage.getItem(PATIENT_CASE_ACTIVITIES_STORAGE_KEY)
    );

    const initialCases =
      storedCases ??
      buildInitialCases(
        currentClinicName,
        currentClinicId,
        alternateClinicName,
        alternateClinicId
      );

    setCases(initialCases.map(normalizePreviewCase));
    setActivities(storedActivities ?? buildInitialActivities());
    setHasLoadedPreview(true);
  }, [
    alternateClinicId,
    alternateClinicName,
    currentClinicId,
    currentClinicName,
    hasLoadedPreview,
  ]);

  useEffect(() => {
    if (!hasLoadedPreview) {
      return;
    }

    window.localStorage.setItem(PATIENT_CASES_STORAGE_KEY, JSON.stringify(cases));
    window.localStorage.setItem(
      PATIENT_CASE_ACTIVITIES_STORAGE_KEY,
      JSON.stringify(activities)
    );
  }, [activities, cases, hasLoadedPreview]);

  const clinicScopedCases = useMemo(() => {
    return cases.filter((patientCase) => patientCase.clinicId === currentClinicId);
  }, [cases, currentClinicId]);

  const previewVisibleCases = clinicScopedCases.length > 0 ? clinicScopedCases : cases;

  const liveCases = useMemo(() => {
    return (patientCasesQuery.data ?? []).map((patientCase) =>
      mapPatientCaseToUiRecord(patientCase, currentClinicName)
    );
  }, [currentClinicName, patientCasesQuery.data]);

  // Browsing and filtering keep every active staff member, so cases already
  // assigned to a non-doctor stay findable. New assignments offer doctors only.
  const liveStaffOwners = useMemo(() => {
    return (ownersQuery.data ?? [])
      .filter((staffMember) => staffMember.status === 'active')
      .map((staffMember) => ({
        id: staffMember.id,
        name: staffMember.name,
        role: staffMember.role,
      }));
  }, [ownersQuery.data]);
  const liveDoctorOwners = useMemo(() => {
    return getPatientDoctorOwners(ownersQuery.data ?? []);
  }, [ownersQuery.data]);

  const liveDataError = useMemo(() => {
    const detailError = isPatientCaseNotFound(patientCaseQuery.error)
      ? null
      : patientCaseQuery.error;

    return patientCasesQuery.error ?? detailError ?? caseActivitiesQuery.error ?? null;
  }, [caseActivitiesQuery.error, patientCaseQuery.error, patientCasesQuery.error]);
  const previewFallbackReason = useMemo(() => {
    if (!liveDataError) {
      return null;
    }

    // A refused session or public key is not "records unavailable": the user is
    // being signed out (see staffSession.handleInvalidSession). Never answer it
    // with preview data, which looks like a working screen of the wrong cases.
    if (isInvalidSessionError(liveDataError) || isPublicKeyRefused(liveDataError)) {
      return null;
    }

    if (isPatientCaseSchemaUnavailable(liveDataError)) {
      return 'Showing the current clinic case list while the main case records are unavailable.';
    }

    return 'Case records are temporarily unavailable, so the current clinic list is shown instead.';
  }, [liveDataError]);

  const shouldUsePreviewData = !currentClinic?.id || previewFallbackReason !== null;
  const ownerOptions = shouldUsePreviewData ? previewOwners : liveStaffOwners;
  const assignableOwnerOptions = shouldUsePreviewData ? previewOwners : liveDoctorOwners;
  const visibleCases = shouldUsePreviewData ? previewVisibleCases : liveCases;

  const filteredCases = useMemo(() => {
    return visibleCases.filter((patientCase) => {
      // Flow fix 2026-07-03: test/demo cases (TESTING DUMMY, "please ignore",
      // Demo ...) were inflating the live action queue. Hidden from every view
      // unless the user explicitly searches - nothing is deleted.
      if (searchTerm.trim().length === 0 && isTestCase(patientCase)) {
        return false;
      }
      // Plato-synced and manually logged cases can leave any of these null.
      const matchesSearch =
        searchTerm.trim().length === 0 ||
        (patientCase.patientDisplayName ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (patientCase.platoPatientNumber ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (patientCase.platoReferenceId ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (patientCase.referringDoctor ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (patientCase.referringClinic ?? '').toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' || patientCase.status === statusFilter;
      const matchesConversion =
        conversionFilter === 'all' || patientCase.conversionStatus === conversionFilter;
      const matchesOwner =
        ownerFilter === 'all' || patientCase.assignedOwnerStaffId === ownerFilter;
      const matchesDateFrom =
        !dateFromFilter ||
        Boolean(patientCase.referralReceivedDate && patientCase.referralReceivedDate >= dateFromFilter);
      const matchesDateTo =
        !dateToFilter ||
        Boolean(patientCase.referralReceivedDate && patientCase.referralReceivedDate <= dateToFilter);
      const matchesCompletion =
        showCompleted === 'all' ||
        ACTIVE_REFERRAL_STATUSES.includes(patientCase.status);
      const finishingSoonRow = finishingSoon.byCaseId.get(patientCase.id);
      const matchesFinishingSoon =
        !finishingSoonOnly ||
        (Boolean(finishingSoonRow) &&
          (finishingSoonDoctor === null || finishingSoonDoctorKey(finishingSoonRow!) === finishingSoonDoctor));

      return (
        matchesFinishingSoon &&
        matchesSearch &&
        matchesStatus &&
        matchesConversion &&
        matchesOwner &&
        matchesDateFrom &&
        matchesDateTo &&
        matchesCompletion
      );
    });
  }, [
    conversionFilter,
    dateFromFilter,
    dateToFilter,
    ownerFilter,
    searchTerm,
    showCompleted,
    statusFilter,
    visibleCases,
    finishingSoon.byCaseId,
    finishingSoonDoctor,
    finishingSoonOnly,
  ]);

  /**
   * Which stat card the user drilled into. Holds the SAME array the card's
   * number came from, so the list and the number cannot drift apart.
   */
  const [statsDrilldown, setStatsDrilldown] = useState<{
    label: string;
    cases: typeof filteredCases;
  } | null>(null);

  /**
   * Export the cases currently in view (Samuel 2026-09-10).
   *
   * Deliberately exports `filteredCases` - the whole filtered set across every
   * page, not just what is on screen - so the CSV matches the filter stack the
   * user set. Scope comes from `visibleCases` upstream, which is already
   * clinic- and permission-scoped, so an admin gets everything they can see and
   * nobody gets more than that.
   */
  const handleExportCases = () => {
    exportRowsToCsv(
      datedFilename('twin-city-cases'),
      filteredCases,
      [
        { header: 'Patient', value: (c) => c.patientDisplayName },
        { header: 'ID', value: (c) => c.platoPatientNumber ?? '' },
        { header: 'Status', value: (c) => getStatusBadgeLabel(c.status) },
        { header: 'Conversion', value: (c) => c.conversionStatus },
        { header: 'Referred by', value: (c) => c.referringDoctor },
        { header: 'Referring clinic', value: (c) => c.referringClinic },
        { header: 'Lead source', value: (c) => formatLeadSource(c.source) },
        { header: 'Referred back', value: (c) => (c.referredBackFlag ? 'Yes' : 'No') },
        { header: 'Referred back date', value: (c) => c.referredBackDate ?? '' },
        { header: 'Next action', value: (c) => c.nextAction },
        { header: 'Next action due', value: (c) => c.nextActionDueDate ?? '' },
        { header: 'Owner', value: (c) => c.assignedOwnerName ?? 'Unassigned' },
      ],
    );
  };

  const priorityQueue = useMemo(() => {
    return [...filteredCases]
      .filter((patientCase) => patientCase.nextActionDueDate)
      .sort((leftCase, rightCase) =>
        (leftCase.nextActionDueDate ?? '').localeCompare(rightCase.nextActionDueDate ?? '')
      )
      .slice(0, 4);
  }, [filteredCases]);

  const selectedPreviewCase = useMemo(() => {
    if (!id) {
      return null;
    }

    return cases.find((patientCase) => patientCase.id === id) ?? null;
  }, [cases, id]);

  const selectedLiveCase = useMemo(() => {
    if (!patientCaseQuery.data) {
      return null;
    }

    return mapPatientCaseToUiRecord(patientCaseQuery.data, currentClinicName);
  }, [currentClinicName, patientCaseQuery.data]);

  const selectedCase = shouldUsePreviewData ? selectedPreviewCase : selectedLiveCase;

  const selectedCaseActivities = useMemo(() => {
    if (!selectedCase) {
      return [];
    }

    if (shouldUsePreviewData) {
      return activities
        .filter((activity) => activity.caseId === selectedCase.id)
        .sort((leftActivity, rightActivity) =>
          rightActivity.createdAt.localeCompare(leftActivity.createdAt)
        );
    }

    return (caseActivitiesQuery.data ?? [])
      .map(mapActivityToPreview)
      .sort((leftActivity, rightActivity) =>
        rightActivity.createdAt.localeCompare(leftActivity.createdAt)
      );
  }, [activities, caseActivitiesQuery.data, selectedCase, shouldUsePreviewData]);

  const referralAttachmentsQuery = useQuery({
    queryKey: ['referralAttachments', selectedCase?.id],
    queryFn: async (): Promise<ReferralAttachmentLink[]> => {
      if (!selectedCase?.id) {
        return [];
      }

      const { data, error } = await supabase.functions.invoke('referral-attachments', {
        body: {
          action: 'list_case_attachments',
          caseId: selectedCase.id,
        },
      });

      if (error || !data?.success) {
        throw new Error(data?.error || error?.message || 'Unable to load referral attachments.');
      }

      return (data.data?.attachments ?? []) as ReferralAttachmentLink[];
    },
    enabled: Boolean(selectedCase?.id && !shouldUsePreviewData),
  });

  const selectedTreatmentRequests = useMemo(
    () => (selectedCase?.treatmentRequests ?? []).filter((item) => item.selected),
    [selectedCase?.treatmentRequests]
  );

  const analyticsSummary = useMemo(() => {
    const total = filteredCases.length;
    const converted = filteredCases.filter((patientCase) => patientCase.conversionStatus === 'converted');
    const notConverted = filteredCases.filter((patientCase) =>
      ['not_converted', 'inactive'].includes(patientCase.conversionStatus)
    );
    const noShow = filteredCases.filter((patientCase) => patientCase.status === 'no_show');
    const nonCompliant = filteredCases.filter((patientCase) => patientCase.status === 'non_compliant');
    const referredBack = filteredCases.filter((patientCase) =>
      isReferralLoopCompleteStatus(patientCase.status)
    );
    const conversionRate = total > 0 ? Math.round((converted.length / total) * 100) : 0;

    const byDoctor = filteredCases.reduce<Record<string, { total: number; converted: number }>>(
      (accumulator, patientCase) => {
        const doctor = patientCase.referringDoctor || 'Unknown doctor';
        accumulator[doctor] ??= { total: 0, converted: 0 };
        accumulator[doctor].total += 1;
        if (patientCase.conversionStatus === 'converted') {
          accumulator[doctor].converted += 1;
        }
        return accumulator;
      },
      {}
    );

    const byClinic = filteredCases.reduce<Record<string, number>>((accumulator, patientCase) => {
      const clinic = patientCase.referringClinic || 'Unknown clinic';
      accumulator[clinic] = (accumulator[clinic] ?? 0) + 1;
      return accumulator;
    }, {});

    const byMonth = filteredCases.reduce<Record<string, number>>((accumulator, patientCase) => {
      const month = patientCase.referralReceivedDate?.slice(0, 7) ?? 'No date';
      accumulator[month] = (accumulator[month] ?? 0) + 1;
      return accumulator;
    }, {});

    // D-022/D-023: 6-month moving average with 40% decline threshold
    const recentMonths = 2; // recent window in months for smoothing
    const baselineMonths = 6; // full moving-average window
    const declineThreshold = 0.4; // 40% decline triggers alert

    const recentCutoff = new Date();
    recentCutoff.setMonth(recentCutoff.getMonth() - recentMonths);
    const baselineCutoff = new Date();
    baselineCutoff.setMonth(baselineCutoff.getMonth() - baselineMonths);
    const recentIso = format(recentCutoff, 'yyyy-MM-dd');
    const baselineIso = format(baselineCutoff, 'yyyy-MM-dd');

    const dropOffAlerts = Object.entries(
      filteredCases.reduce<Record<string, { recentCount: number; baselineCount: number }>>(
        (accumulator, patientCase) => {
          const doctor = patientCase.referringDoctor || 'Unknown doctor';
          accumulator[doctor] ??= { recentCount: 0, baselineCount: 0 };

          if (patientCase.referralReceivedDate && patientCase.referralReceivedDate >= baselineIso) {
            accumulator[doctor].baselineCount += 1;
            if (patientCase.referralReceivedDate >= recentIso) {
              accumulator[doctor].recentCount += 1;
            }
          }

          return accumulator;
        },
        {}
      )
    )
      .map(([doctor, stats]) => {
        const avgPerMonth = stats.baselineCount / baselineMonths;
        const recentPerMonth = stats.recentCount / recentMonths;
        const declinePct = avgPerMonth > 0 ? (avgPerMonth - recentPerMonth) / avgPerMonth : 0;
        return {
          doctor,
          avgPerMonth: Math.round(avgPerMonth * 10) / 10,
          recentPerMonth: Math.round(recentPerMonth * 10) / 10,
          declinePct: Math.round(declinePct * 100),
        };
      })
      .filter((row) => row.avgPerMonth > 0 && row.declinePct >= declineThreshold * 100)
      .sort((left, right) => right.declinePct - left.declinePct);

    return {
      total,
      converted: converted.length,
      notConverted: notConverted.length,
      noShow: noShow.length,
      nonCompliant: nonCompliant.length,
      referredBack: referredBack.length,
      conversionRate,
      /**
       * The cases behind each number, so clicking a stat card can show WHO it
       * counted (Samuel 2026-09-10). These are the very same arrays the counts
       * above are taken from, so the list can never disagree with the number.
       */
      lists: {
        total: filteredCases,
        converted,
        notConverted,
        noShow,
        nonCompliant,
        referredBack,
      },
      byDoctor: Object.entries(byDoctor)
        .map(([doctor, stats]) => ({
          doctor,
          ...stats,
          rate: stats.total > 0 ? Math.round((stats.converted / stats.total) * 100) : 0,
        }))
        .sort((left, right) => right.total - left.total),
      byClinic: Object.entries(byClinic)
        .map(([clinic, totalCases]) => ({ clinic, totalCases }))
        .sort((left, right) => right.totalCases - left.totalCases),
      byMonth: Object.entries(byMonth)
        .map(([month, totalCases]) => ({ month, totalCases }))
        .sort((left, right) => left.month.localeCompare(right.month)),
      dropOffAlerts,
    };
  }, [filteredCases]);

  const handleCreateCase = () => {
    setEditingCase(null);
    setModalOpen(true);
  };

  const handleReferralSync = async () => {
    setIsSyncingReferrals(true);
    try {
      // Pull staff edits back from Plato: real patient names + any referral
      // that has been moved out of the waitlist into a doctor's calendar.
      // Scope to the cases currently in view (not every patient).
      const caseIds = filteredCases.map((patientCase) => patientCase.id);
      const { data, error } = await supabase.functions.invoke('plato-referral-pull', {
        body: { case_ids: caseIds },
      });

      if (error) {
        throw error;
      }

      await patientCasesQuery.refetch();

      const renamed = (data as { renamed?: number } | null)?.renamed ?? 0;
      const scheduled = (data as { scheduled?: number } | null)?.scheduled ?? 0;
      toast({
        title: 'Synced with Plato',
        description:
          renamed || scheduled
            ? `${renamed} name(s) updated, ${scheduled} now scheduled.`
            : 'No changes from Plato.',
      });
    } catch (error) {
      console.error('Referral sync failed:', error);
      toast({
        title: 'Plato sync failed',
        description: 'Could not pull updates from Plato. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSyncingReferrals(false);
    }
  };

  const handleEditCase = (patientCase: PatientCaseUiRecord) => {
    setEditingCase(patientCase);
    setModalOpen(true);
  };

  const handleToggleReferredBack = async (
    patientCase: PatientCaseUiRecord,
    nextFlag: boolean
  ) => {
    const today = format(new Date(), 'yyyy-MM-dd');

    if (nextFlag) {
      const confirmed = window.confirm(
        `Mark ${patientCase.patientDisplayName} as referred back to ${
          patientCase.referringClinic || 'the referring clinic'
        }? The referring clinic will be notified by email.`
      );
      if (!confirmed) {
        return;
      }
    } else {
      const confirmed = window.confirm(
        `Clear the referred-back flag for ${patientCase.patientDisplayName}? No notification will be sent.`
      );
      if (!confirmed) {
        return;
      }
    }

    // Live mode: dedicated mutation that flips the column flag and (best-effort)
    // fires the notification on a false->true transition.
    if (!shouldUsePreviewData && currentClinic?.id && user?.id) {
      try {
        await setReferredBackFlagLive({
          caseId: patientCase.id,
          nextFlag,
          currentUserId: user.id,
          referredBackDate: nextFlag ? patientCase.referredBackDate ?? today : null,
        });
        return;
      } catch (error) {
        console.error('Live referred-back flip failed; falling back to preview mode:', error);
      }
    }

    // Preview / fallback: update local state and log an activity.
    const now = new Date().toISOString();
    setCases((currentCases) =>
      currentCases.map((existing) =>
        existing.id === patientCase.id
          ? {
              ...existing,
              referredBackFlag: nextFlag,
              referredBackDate: nextFlag ? existing.referredBackDate ?? today : null,
              updatedAt: now,
            }
          : existing
      )
    );
    setActivities((currentActivities) => [
      {
        id: `activity-${Date.now()}-referred-flip`,
        caseId: patientCase.id,
        activityType: 'referred_back',
        title: nextFlag ? 'Marked as referred back' : 'Referred-back flag cleared',
        detail: nextFlag
          ? `Patient referred back to ${
              patientCase.referringClinic || 'the referring clinic'
            } on ${formatDisplayDate(today)}.`
          : 'Referred-back flag cleared by clinic user.',
        actor: 'Clinic user',
        createdAt: now,
      },
      ...currentActivities,
    ]);
  };

  const handleQuickStatusChange = async (
    patientCase: PatientCaseUiRecord,
    nextStatus: PatientCaseUiStatus
  ) => {
    const referralLoopComplete = isReferralLoopCompleteStatus(nextStatus);
    await handleSaveCase(
      {
        patientDisplayName: patientCase.patientDisplayName,
        platoReferenceId: patientCase.platoReferenceId,
        referringDoctor: patientCase.referringDoctor,
        referringClinic: patientCase.referringClinic,
        referralChannel: patientCase.referralChannel,
        // Preserve the existing lead source on a quick status change.
        source: patientCase.source ?? DEFAULT_MANUAL_LEAD_SOURCE,
        referralReceivedDate: patientCase.referralReceivedDate,
        status: nextStatus,
        conversionStatus: getReferralConversionStatus(nextStatus),
        conversionDate: patientCase.conversionDate,
        nextAction: patientCase.nextAction,
        nextActionDueDate: patientCase.nextActionDueDate,
        assignedOwnerStaffId: patientCase.assignedOwnerStaffId,
        referredBackFlag: referralLoopComplete,
        referredBackDate: referralLoopComplete ? patientCase.referredBackDate : null,
        remarks: patientCase.remarks,
        externalStatusNote: patientCase.externalStatusNote,
      },
      patientCase
    );
  };

  const handleSaveCase = async (
    values: PatientCaseFormValues,
    targetCase: PatientCaseUiRecord | null = editingCase
  ) => {
    const now = new Date().toISOString();
    const today = format(new Date(), 'yyyy-MM-dd');
    const nextConversionStatus = getReferralConversionStatus(values.status);
    const conversionStatus =
      targetCase && nextConversionStatus === 'pending'
        ? targetCase.conversionStatus
        : nextConversionStatus;
    const conversionDate =
      targetCase && nextConversionStatus === 'pending'
        ? targetCase.conversionDate
        : conversionStatus === 'converted'
          ? values.conversionDate ?? targetCase?.conversionDate ?? today
          : null;
    const referralLoopComplete = isReferralLoopCompleteStatus(values.status);
    const referredBackChanged = Boolean(
      targetCase?.referredBackFlag &&
      referralLoopComplete &&
      values.referredBackDate !== targetCase.referredBackDate
    );
    const referredBackFlag = targetCase?.referredBackFlag ?? referralLoopComplete;
    const referredBackDate = targetCase
      ? referredBackChanged
        ? values.referredBackDate
        : targetCase.referredBackDate
      : referralLoopComplete
        ? values.referredBackDate ?? today
        : null;
    const conversionChanged =
      !targetCase ||
      conversionStatus !== targetCase.conversionStatus ||
      conversionDate !== targetCase.conversionDate;

    if (!shouldUsePreviewData && currentClinic?.id && user?.id) {
      const platoReferenceId = values.platoReferenceId?.trim();
      const livePayload: Omit<
        PatientCaseInsert,
        | 'created_by'
        | 'plato_reference_id'
        | 'conversion_status'
        | 'conversion_date'
        | 'referred_back_flag'
        | 'referred_back_date'
      > = {
        patient_display_name: values.patientDisplayName.trim(),
        referring_doctor: values.referringDoctor.trim() || null,
        referring_clinic: values.referringClinic.trim() || null,
        referral_channel: values.referralChannel || null,
        referral_received_date: values.referralReceivedDate,
        clinic_id: currentClinic.id,
        status: values.status,
        next_action: values.nextAction.trim() || null,
        next_action_due_date: values.nextActionDueDate,
        assigned_owner_staff_id: values.assignedOwnerStaffId,
        remarks: values.remarks.trim() || null,
        external_status_note: values.externalStatusNote.trim() || null,
      };

      // `source` (lead origin) exists in the DB but is missing from the stale
      // generated types — attach it via cast. Website/Portal auto-tag on their
      // own entry paths; here we persist the internal/WhatsApp choice.
      const payloadWithSource = {
        ...livePayload,
        ...(values.source ? { source: values.source } : {}),
      };

      try {
        if (targetCase) {
          // Union of the lead-source work (payloadWithSource carries `source`)
          // and audit wave A (C9/C10): plato/conversion/referred-back fields go
          // in ONLY when actually changed, so status edits can't clobber them.
          const updates = {
            ...payloadWithSource,
            ...(platoReferenceId ? { plato_reference_id: platoReferenceId } : {}),
            ...(conversionChanged
              ? {
                  conversion_status: conversionStatus,
                  conversion_date: conversionDate,
                }
              : {}),
            ...(referredBackChanged
              ? {
                  referred_back_flag: referredBackFlag,
                  referred_back_date: referredBackDate,
                }
              : {}),
          } as PatientCaseUpdate;

          await updateLivePatientCase({
            caseId: targetCase.id,
            updates,
            currentUserId: user.id,
          });
        } else {
          if (!platoReferenceId) {
            throw new Error('ID is required to create a patient case.');
          }

          const createdCase = await createLivePatientCase({
            ...payloadWithSource,
            plato_reference_id: platoReferenceId,
            conversion_status: conversionStatus,
            conversion_date: conversionDate,
            referred_back_flag: referredBackFlag,
            referred_back_date: referredBackDate,
            created_by: user.id,
          } as PatientCaseInsert);
          navigate(`/patients/${createdCase.id}`);
        }

        setModalOpen(false);
        setEditingCase(null);
        return;
      } catch (error) {
        console.error('Patient case save failed:', error);
        return;
      }
    }

    if (targetCase) {
      const updatedCase: PatientCaseUiRecord = {
        ...targetCase,
        ...values,
        conversionStatus,
        conversionDate,
        referredBackFlag,
        referredBackDate,
        assignedOwnerName:
          ownerOptions.find((owner) => owner.id === values.assignedOwnerStaffId)?.name ??
          (values.assignedOwnerStaffId === targetCase.assignedOwnerStaffId
            ? targetCase.assignedOwnerName
            : null),
        clinicId: targetCase.clinicId,
        clinicName: targetCase.clinicName,
        externalStatusNote: values.externalStatusNote,
        statusChangedAt: targetCase.status !== values.status ? now : targetCase.statusChangedAt,
        treatmentAcceptedAt:
          conversionStatus === 'converted'
            ? targetCase.treatmentAcceptedAt ?? now
            : targetCase.treatmentAcceptedAt,
        completedAt: isReferralLoopCompleteStatus(values.status) ? targetCase.completedAt ?? now : targetCase.completedAt,
        inactiveAt:
          ['patient_did_not_proceed', 'lost_inactive', 'no_show', 'non_compliant'].includes(values.status)
            ? targetCase.inactiveAt ?? now
            : targetCase.inactiveAt,
        updatedAt: now,
      };

      setCases((currentCases) =>
        currentCases.map((patientCase) =>
          patientCase.id === targetCase.id ? updatedCase : patientCase
        )
      );

      const newActivities: PatientCaseActivityPreview[] = [];

      if (targetCase.status !== values.status) {
        newActivities.push({
          id: `activity-${Date.now()}-status`,
          caseId: targetCase.id,
          activityType: 'status_change',
          title: 'Status updated',
          detail: `${STATUS_META[targetCase.status].label} -> ${STATUS_META[values.status].label}`,
          actor: 'Clinic user',
          createdAt: now,
        });
      }

      if (targetCase.assignedOwnerStaffId !== values.assignedOwnerStaffId) {
        const fromOwner = targetCase.assignedOwnerName ?? 'Unassigned';
        const toOwner =
          ownerOptions.find((owner) => owner.id === values.assignedOwnerStaffId)?.name ??
          'Unassigned';

        newActivities.push({
          id: `activity-${Date.now()}-owner`,
          caseId: targetCase.id,
          activityType: 'ownership_change',
          title: 'Ownership changed',
          detail: `${fromOwner} -> ${toOwner}`,
          actor: 'Clinic user',
          createdAt: now,
        });
      }

      if (!targetCase.referredBackFlag && updatedCase.referredBackFlag) {
        newActivities.push({
          id: `activity-${Date.now()}-referred`,
          caseId: targetCase.id,
          activityType: 'referred_back',
          title: 'Case marked as referred back',
          detail:
            updatedCase.referredBackDate != null
              ? `Referred back on ${formatDisplayDate(updatedCase.referredBackDate)}.`
              : 'Marked as referred back without a date.',
          actor: 'Clinic user',
          createdAt: now,
        });
      }

      if (values.remarks !== targetCase.remarks && values.remarks.trim().length > 0) {
        newActivities.push({
          id: `activity-${Date.now()}-note`,
          caseId: targetCase.id,
          activityType: 'note',
          title: 'Operational note updated',
          detail: values.remarks.trim(),
          actor: 'Clinic user',
          createdAt: now,
        });
      }

      if (newActivities.length > 0) {
        setActivities((currentActivities) => [...newActivities, ...currentActivities]);
      }
    } else {
      const ownerName =
        ownerOptions.find((owner) => owner.id === values.assignedOwnerStaffId)?.name ?? null;
      const newCaseId = `case-preview-${Date.now()}`;

      const newCase: PatientCaseUiRecord = {
        id: newCaseId,
        ...values,
        conversionStatus,
        conversionDate,
        referredBackFlag,
        referredBackDate,
        clinicId: currentClinicId,
        clinicName: currentClinicName,
        assignedOwnerName: ownerName,
        externalStatusNote: values.externalStatusNote,
        statusChangedAt: now,
        treatmentAcceptedAt: conversionStatus === 'converted' ? now : null,
        completedAt: isReferralLoopCompleteStatus(values.status) ? now : null,
        inactiveAt: ['patient_did_not_proceed', 'lost_inactive', 'no_show', 'non_compliant'].includes(values.status)
          ? now
          : null,
        createdAt: now,
        updatedAt: now,
      };

      setCases((currentCases) => [newCase, ...currentCases]);
      setActivities((currentActivities) => [
        {
          id: `activity-${Date.now()}-created`,
          caseId: newCaseId,
          activityType: 'note',
          title: 'Case created',
          detail: `${newCase.patientDisplayName} added to the case list for ${newCase.clinicName}.`,
          actor: 'Clinic user',
          createdAt: now,
        },
        ...currentActivities,
      ]);
    }

    setModalOpen(false);
    setEditingCase(null);
  };

  const handleDeleteCase = async (targetCase: PatientCaseUiRecord) => {
    // Live delete first; if it fails (or we're in preview mode), fall through
    // to removing it from local preview state so the UI stays consistent.
    if (!shouldUsePreviewData && targetCase.id) {
      try {
        await deleteLivePatientCase({
          caseId: targetCase.id,
          patientName: targetCase.patientDisplayName,
        });
      } catch (error) {
        console.error('Falling back to local preview delete after live mutation failure:', error);
      }
    }

    setCases((currentCases) =>
      currentCases.filter((patientCase) => patientCase.id !== targetCase.id)
    );
    setActivities((currentActivities) =>
      currentActivities.filter((activity) => activity.caseId !== targetCase.id)
    );

    setModalOpen(false);
    setEditingCase(null);

    // If we were on the case-detail route, go back to the list.
    if (id === targetCase.id) {
      navigate('/patients');
    }
  };

  if (id) {
    if (
      !shouldUsePreviewData &&
      (patientCaseQuery.isLoading || caseActivitiesQuery.isLoading)
    ) {
      return (
        <div className="flex w-full flex-col gap-6">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="flex min-h-[220px] items-center justify-center text-sm text-slate-600">
              Loading patient case...
            </CardContent>
          </Card>
        </div>
      );
    }

    if (!selectedCase) {
      const isLiveNotFound =
        !shouldUsePreviewData && isPatientCaseNotFound(patientCaseQuery.error);

      return (
        <div className="flex w-full flex-col gap-6">
          <div className="flex items-center justify-between">
            <div>
              <Badge variant="outline" className="mb-3">
                Patients
              </Badge>
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
                Patient case not found
              </h2>
              <p className="mt-2 max-w-2xl text-sm text-slate-600">
                {isLiveNotFound
                  ? 'This patient case does not exist in the current clinic dataset.'
                  : 'This case is not in the current clinic list.'}
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/patients')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Patients
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">Patients</Badge>
              <Badge className={cn('border', STATUS_META[selectedCase.status].badgeClassName)}>
                {getStatusBadgeLabel(selectedCase.status)}
              </Badge>
              <Badge
                variant="outline"
                className={cn('border', CONVERSION_META[selectedCase.conversionStatus].badgeClassName)}
              >
                {CONVERSION_META[selectedCase.conversionStatus].label}
              </Badge>
              {isManualReferralCase(selectedCase) && <ManualReferralBadge />}
              {/* Lead source — internal/admin only; never in the referring-dentist portal. */}
              <Badge variant="outline" className={cn('border-0', leadSourceBadgeClass(selectedCase.source))}>
                Source: {formatLeadSource(selectedCase.source)}
              </Badge>
              {isReferralLoopCompleteStatus(selectedCase.status) && (
                <Badge variant="secondary">Referral loop complete</Badge>
              )}
              <ReferredBackIndicator
                referredBackFlag={selectedCase.referredBackFlag}
                referredBackDate={selectedCase.referredBackDate}
                status={selectedCase.status}
                disabled={isFlippingReferredBack || isUpdating}
                onToggle={(nextFlag) => handleToggleReferredBack(selectedCase, nextFlag)}
              />
            </div>

            <div>
              <h2 className="text-2xl font-semibold tracking-tight text-slate-950">
                {selectedCase.patientDisplayName}
              </h2>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-slate-600">
                <span className="inline-flex items-center gap-2">
                  <ExternalLink className="h-4 w-4 text-slate-400" />
                  ID {selectedCase.platoPatientNumber || '-'}
                </span>
                <span className="inline-flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-slate-400" />
                  {selectedCase.clinicName}
                </span>
                <span className="inline-flex items-center gap-2">
                  <UserRound className="h-4 w-4 text-slate-400" />
                  {selectedCase.assignedOwnerName ?? 'Unassigned'}
                </span>
              </div>
            </div>

            <div className="grid gap-2.5 sm:grid-cols-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5">
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Next action</p>
                <p className="mt-1.5 text-sm font-medium text-slate-900">{selectedCase.nextAction}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5">
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Due</p>
                <p className="mt-1.5 text-sm font-medium text-slate-900">
                  {getDaysUntilDue(selectedCase.nextActionDueDate) ?? 'No due date'}
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5">
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Status changed</p>
                <p className="mt-1.5 text-sm font-medium text-slate-900">
                  {format(parseISO(selectedCase.statusChangedAt), 'dd MMM yyyy, h:mm a')}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => navigate('/patients')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button onClick={() => handleEditCase(selectedCase)}>
              Edit Case
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle>Case stage</CardTitle>
            <CardDescription>
              One ladder for the whole referral. Tap the stage this case is at now;
              Scheduled and Seen update themselves from Plato once the patient is linked.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* ONE pipeline (flow fix 2026-07-03): the same stages the list and
                board show, as a single stepper with the current stage lit. This
                replaces the old split control ("Treatment progress" segments +
                a hidden "Other outcomes" disclosure) that made a case's position
                impossible to read: a New Referral showed three unlit treatment
                buttons and nothing else. */}
            <div className="flex flex-wrap items-center gap-y-2">
              {MAIN_PIPELINE_STATUSES.map((status, index) => {
                const currentIndex = MAIN_PIPELINE_STATUSES.indexOf(
                  selectedCase.status as PatientCaseUiStatus,
                );
                const active = selectedCase.status === status;
                const passed = currentIndex >= 0 && index < currentIndex;
                return (
                  <div key={status} className="flex items-center">
                    {index > 0 && <ChevronRight className="mx-0.5 h-3.5 w-3.5 shrink-0 text-slate-300" />}
                    <Button
                      variant={active ? 'default' : passed ? 'secondary' : 'outline'}
                      size="sm"
                      className={passed ? 'text-slate-500' : undefined}
                      disabled={active || isUpdating || isCreating}
                      onClick={() => handleQuickStatusChange(selectedCase, status)}
                    >
                      {passed && <CheckCircle2 className="mr-1 h-3.5 w-3.5 text-emerald-600" />}
                      {MAIN_PIPELINE_LABELS[status] ?? STATUS_META[status].columnLabel}
                    </Button>
                  </div>
                );
              })}
            </div>

            {/* Off-ramps stay visible (not hidden behind a disclosure) but quiet. */}
            <div>
              <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
                Close or divert
              </p>
              <div className="flex flex-wrap gap-2">
                {OFF_RAMP_STATUSES.map((status) => (
                  <Button
                    key={status}
                    variant={selectedCase.status === status ? 'default' : 'outline'}
                    size="sm"
                    disabled={selectedCase.status === status || isUpdating || isCreating}
                    onClick={() => handleQuickStatusChange(selectedCase, status)}
                  >
                    {STATUS_META[status].label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {shouldUsePreviewData ? (
          <Alert className="border-slate-200 bg-slate-50">
            <BellRing className="h-4 w-4" />
            <AlertTitle>Case record</AlertTitle>
            <AlertDescription>
              {previewFallbackReason ??
                'Review referral owner, status, and next action from the current clinic list.'}
            </AlertDescription>
          </Alert>
        ) : (
          <Alert className="border-slate-200 bg-slate-50">
            <CheckCircle2 className="h-4 w-4" />
            <AlertTitle>Live case record</AlertTitle>
            <AlertDescription>
              This case is loading from the clinic case records.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 xl:grid-cols-[1.7fr_1fr]">
          <div className="space-y-4">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle>Referral Source</CardTitle>
                <CardDescription>
                  Key referral details for triage, follow-up, and handoff.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Doctor</p>
                  <p className="mt-2 text-sm font-medium text-slate-900">
                    {selectedCase.referringDoctor}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Clinic</p>
                  <p className="mt-2 text-sm font-medium text-slate-900">
                    {selectedCase.referringClinic}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                    Referral channel
                  </p>
                  <p className="mt-2 text-sm font-medium capitalize text-slate-900">
                    {selectedCase.referralChannel}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                    Referral received
                  </p>
                  <p className="mt-2 text-sm font-medium text-slate-900">
                    {formatDisplayDate(selectedCase.referralReceivedDate)}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                    Conversion
                  </p>
                  <p className="mt-2 text-sm font-medium text-slate-900">
                    {CONVERSION_META[selectedCase.conversionStatus].label}
                    {selectedCase.conversionDate
                      ? ` on ${formatDisplayDate(selectedCase.conversionDate)}`
                      : ''}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Button
              variant="outline"
              size="sm"
              className="w-full justify-center text-slate-600 hover:text-slate-900"
              onClick={() => setShowCaseReference((current) => !current)}
            >
              {showCaseReference ? (
                <ChevronDown className="mr-1.5 h-4 w-4" />
              ) : (
                <ChevronRight className="mr-1.5 h-4 w-4" />
              )}
              {showCaseReference
                ? 'Hide full case details'
                : 'Show full case details (referral form, notes, history)'}
            </Button>

            {showCaseReference && (
              <>
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle>Referral Intake</CardTitle>
                <CardDescription>
                  Submitted by the external dentist portal from the online referral form.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">NRIC</p>
                    <p className="mt-2 text-sm font-medium text-slate-900">
                      {selectedCase.patientNric || 'Not provided'}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">HP/Tel</p>
                    <p className="mt-2 text-sm font-medium text-slate-900">
                      {selectedCase.patientPhone || 'Not provided'}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                      Clinic Email
                    </p>
                    <p className="mt-2 break-words text-sm font-medium text-slate-900">
                      {selectedCase.clinicEmail || 'Not provided'}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                      Clinic Branch
                    </p>
                    <p className="mt-2 text-sm font-medium text-slate-900">
                      {selectedCase.clinicBranch || 'Not provided'}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white p-4 lg:col-span-2">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                      Preferred Specialist
                    </p>
                    <p className="mt-2 text-sm font-medium text-slate-900">
                      {selectedCase.preferredSpecialist || 'Not provided'}
                    </p>
                  </div>
                </div>

                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Treatment Needed</TableHead>
                        <TableHead className="w-48">Tooth Number</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedTreatmentRequests.length > 0 ? (
                        selectedTreatmentRequests.map((request) => (
                          <TableRow key={request.key}>
                            <TableCell className="font-medium text-slate-800">
                              {request.label}
                            </TableCell>
                            <TableCell>{request.toothNumber || '-'}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={2} className="text-sm text-slate-500">
                            No treatment rows were selected.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                    Referral Notes
                  </p>
                  <p className="mt-3 whitespace-pre-line text-sm leading-6 text-slate-700">
                    {selectedCase.submissionNote || 'No referral notes submitted.'}
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                        Attachments
                      </p>
                      <p className="mt-1 text-sm text-slate-600">
                        Referral files are private and opened through time-limited links.
                      </p>
                    </div>
                    {referralAttachmentsQuery.isFetching && (
                      <Badge variant="outline">Loading files</Badge>
                    )}
                  </div>

                  <div className="mt-4 space-y-2">
                    {referralAttachmentsQuery.isError ? (
                      <p className="text-sm text-rose-700">
                        Unable to load attachments. Please try opening this case again.
                      </p>
                    ) : referralAttachmentsQuery.data && referralAttachmentsQuery.data.length > 0 ? (
                      referralAttachmentsQuery.data.map((attachment) => (
                        <a
                          key={attachment.id}
                          href={attachment.signedUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="flex flex-col gap-1 rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-800 transition hover:border-slate-300 hover:bg-slate-50 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <span className="font-medium">{attachment.fileName}</span>
                          <span className="flex items-center gap-2 text-xs text-slate-500">
                            {formatFileSize(attachment.byteSize)}
                            <ExternalLink className="h-3.5 w-3.5" />
                          </span>
                        </a>
                      ))
                    ) : (
                      <p className="text-sm text-slate-500">No attachments uploaded.</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle>Operational Narrative</CardTitle>
                <CardDescription>
                  Short, non-clinical notes that keep the case moving without becoming a second
                  record system.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Remarks</p>
                  <p className="mt-3 text-sm leading-6 text-slate-700">{selectedCase.remarks}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Scheduling</p>
                  <p className="mt-3 text-sm leading-6 text-slate-700">
                    Related appointments can appear here once case records and scheduler items share
                    the same patient reference.
                  </p>
                </div>
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-emerald-700">
                    External-safe update
                  </p>
                  <p className="mt-3 text-sm leading-6 text-emerald-950">
                    {selectedCase.externalStatusNote ||
                      'No external-safe update has been written yet.'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle>Activity Timeline</CardTitle>
                <CardDescription>
                  Lightweight operational history, tuned for traceability rather than full
                  communication archiving.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-5">
                  {selectedCaseActivities.map((activity, index) => {
                    const ActivityIcon = getActivityIcon(activity.activityType);

                    return (
                      <div key={activity.id} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-slate-50">
                            <ActivityIcon className="h-4 w-4 text-slate-700" />
                          </div>
                          {index < selectedCaseActivities.length - 1 && (
                            <div className="mt-2 h-full w-px bg-slate-200" />
                          )}
                        </div>
                        <div className="flex-1 pb-4">
                          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                            <h3 className="text-sm font-semibold text-slate-900">{activity.title}</h3>
                            <p className="text-xs uppercase tracking-[0.16em] text-slate-500">
                              {format(parseISO(activity.createdAt), 'dd MMM, h:mm a')}
                            </p>
                          </div>
                          <p className="mt-2 text-sm leading-6 text-slate-700">{activity.detail}</p>
                          <p className="mt-2 text-xs text-slate-500">By {activity.actor}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
              </>
            )}
          </div>

          <div className="space-y-4">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle>Case Pulse</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Assigned owner</p>
                  <p className="mt-2 text-sm font-medium text-slate-900">
                    {selectedCase.assignedOwnerName ?? 'Unassigned'}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  {/* Flow fix 2026-07-03: show the case's ACTUAL stage. The old
                      "Referral loop" line said "Ongoing treatment" for every
                      non-closed case - including brand-new referrals - directly
                      contradicting the status badge above it. */}
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Current stage</p>
                  <p className="mt-2 text-sm font-medium text-slate-900">
                    {STATUS_META[selectedCase.status as PatientCaseUiStatus]?.label ?? selectedCase.status}
                  </p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Plato link</p>
                  <p className="mt-2 text-sm font-medium text-slate-900">
                    {selectedCase.platoReferenceId
                      ? 'Linked - Scheduled/Seen update automatically'
                      : 'Not linked - statuses will NOT auto-update from Plato'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <PatientVisitHistory platoReferenceId={selectedCase.platoReferenceId ?? null} />

            {/* (Removed 2026-07-03: the static "Case workflow" checklist was
                marketing filler on a work screen - four always-green ticks that
                described the app instead of the case.) */}
          </div>
        </div>

        <PatientCaseModal
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setEditingCase(null);
          }}
          onSave={handleSaveCase}
          onDelete={handleDeleteCase}
          isDeleting={isDeleting}
          owners={assignableOwnerOptions}
          clinicName={selectedCase.clinicName}
          patientCase={editingCase}
        />
      </div>
    );
  }

  if (!shouldUsePreviewData && (patientCasesQuery.isLoading || ownersQuery.isLoading)) {
    return (
      <div className="flex w-full flex-col gap-6">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex min-h-[240px] items-center justify-center text-sm text-slate-600">
            Loading patient cases...
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="rounded-2xl border border-slate-200 bg-white px-5 py-3.5 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="w-fit">
              Patients
            </Badge>
            <h2 className="text-lg font-semibold tracking-tight text-slate-950">
              Referrals &amp; case management
            </h2>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className="bg-white">
              <BriefcaseMedical className="mr-2 h-4 w-4" />
              {currentClinicName}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="bg-white"
              onClick={handleReferralSync}
              disabled={isSyncingReferrals}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {isSyncingReferrals ? 'Syncing...' : 'Sync Plato'}
            </Button>
            <Button size="sm" onClick={handleCreateCase}>
              <Plus className="mr-2 h-4 w-4" />
              New Case
            </Button>
          </div>
        </div>
      </div>

      {/* Per-screen purpose banner — calm, plain-English orientation line so the
          board reads as a clear workflow, not a wall of features. */}
      <div className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-600">
        <ClipboardList className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
        <p>
          Track every referred patient from referral to discharge. Tap a patient to
          see their details and update their progress.
        </p>
      </div>

      {previewFallbackReason && (
        <Alert className="border-amber-200 bg-amber-50 text-amber-950">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Case list</AlertTitle>
          <AlertDescription>{previewFallbackReason}</AlertDescription>
        </Alert>
      )}

      <Tabs
        value={activeTopTab}
        onValueChange={handleTopTabChange}
        className="space-y-4"
      >
        <div className="flex flex-col gap-2.5 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="list">Case list</TabsTrigger>
            <TabsTrigger value="board">Operations board</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="reconciliation">Expected vs arrived</TabsTrigger>
            <TabsTrigger value="referring-dentists">Referring dentists</TabsTrigger>
            <TabsTrigger value="referral-caselist">Referral case list</TabsTrigger>
          </TabsList>

          {/*
            Case filters drive `filteredCases`, which feeds the Case list,
            Operations board, and Analytics tabs. The Referring dentists tab is
            a directory with its own search + filters, so hide these there to
            avoid two competing search bars.
          */}
          {activeTopTab !== 'referring-dentists' && activeTopTab !== 'reconciliation' && activeTopTab !== 'referral-caselist' && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-full sm:w-52">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search patient or ID"
                className="h-9 pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.entries(STATUS_META).map(([status, meta]) => (
                  <SelectItem key={status} value={status}>
                    {meta.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={conversionFilter} onValueChange={setConversionFilter}>
              <SelectTrigger className="h-9 w-[140px]">
                <SelectValue placeholder="Conversion" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All conversion</SelectItem>
                {Object.entries(CONVERSION_META).map(([status, meta]) => (
                  <SelectItem key={status} value={status}>
                    {meta.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={ownerFilter} onValueChange={setOwnerFilter}>
              <SelectTrigger className="h-9 w-[130px]">
                <SelectValue placeholder="Owner" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All owners</SelectItem>
                {ownerOptions.map((owner) => (
                  <SelectItem key={owner.id} value={owner.id}>
                    {owner.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={showCompleted}
              onValueChange={(value: 'active' | 'all') => setShowCompleted(value)}
            >
              <SelectTrigger className="h-9 w-[140px]">
                <SelectValue placeholder="Visibility" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active only</SelectItem>
                <SelectItem value="all">Include closed</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={dateFromFilter}
              onChange={(event) => setDateFromFilter(event.target.value)}
              aria-label="Referral date from"
              className="h-9 w-[150px]"
            />
            <Input
              type="date"
              value={dateToFilter}
              onChange={(event) => setDateToFilter(event.target.value)}
              aria-label="Referral date to"
              className="h-9 w-[150px]"
            />
          </div>
          )}
          {activeTopTab !== 'referring-dentists' && activeTopTab !== 'reconciliation' && activeTopTab !== 'referral-caselist' && (
            <FinishingSoonBar
              count={finishingSoon.count}
              doctors={finishingSoon.doctors}
              active={finishingSoonOnly}
              onToggle={() => {
                setFinishingSoonOnly((current) => !current);
                setFinishingSoonDoctor(null);
              }}
              doctorKey={finishingSoonDoctor}
              onDoctorChange={setFinishingSoonDoctor}
              lastRun={finishingSoon.lastRun}
              isLoading={finishingSoon.isLoading}
            />
          )}
        </div>

        <TabsContent value="list" className="space-y-4">
          <div className="grid gap-4">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <CardTitle>Cases in view</CardTitle>
                  <CardDescription>
                    {filteredCases.length} cases match the current clinic and filter stack.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportCases}
                    disabled={filteredCases.length === 0}
                    title="Download these cases as a CSV, matching the filters above"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Export
                  </Button>
                  <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs uppercase tracking-[0.18em] text-slate-500">
                    <Filter className="h-3.5 w-3.5" />
                    Case filters
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Patient</TableHead>
                      <TableHead>Referral source</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Referred back</TableHead>
                      <TableHead>Next action</TableHead>
                      <TableHead>Owner</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCases.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="py-10 text-center">
                          <p className="text-sm font-medium text-slate-700">
                            No referrals yet
                          </p>
                          <p className="mt-1 text-sm text-slate-500">
                            They appear here automatically from the website and dentist portal.
                          </p>
                        </TableCell>
                      </TableRow>
                    )}
                    {filteredCases.map((patientCase) => (
                      <TableRow key={patientCase.id}>
                        <TableCell>
                          <div className="space-y-1">
                            <button
                              type="button"
                              onClick={() => navigate(`/patients/${patientCase.id}`)}
                              className="text-left text-sm font-semibold text-slate-900 transition hover:text-primary"
                            >
                              {patientCase.patientDisplayName}
                            </button>
                            {patientCase.platoPatientNumber && (
                              <p className="text-xs uppercase tracking-[0.16em] text-slate-500">
                                ID {patientCase.platoPatientNumber}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm font-medium text-slate-800">
                            {patientCase.referringDoctor}
                          </p>
                          <p className="text-xs text-slate-500">{patientCase.referringClinic}</p>
                          {/* Lead source — internal/admin only; never shown in the referring-dentist portal. */}
                          <span
                            className={cn(
                              'mt-1 inline-block rounded px-1.5 py-0 text-[10px] font-medium',
                              leadSourceBadgeClass(patientCase.source),
                            )}
                          >
                            {formatLeadSource(patientCase.source)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <Badge
                              variant="outline"
                              className={cn('border', STATUS_META[patientCase.status].badgeClassName)}
                            >
                              {getStatusBadgeLabel(patientCase.status)}
                            </Badge>
                            {isManualReferralCase(patientCase) && (
                              <ManualReferralBadge compact />
                            )}
                            {(patientCase as any).platoWaitlistPushedAt && (
                              <Badge
                                variant="outline"
                                className="border-emerald-300 bg-emerald-50 text-emerald-800 text-[10px] px-1.5 py-0"
                              >
                                Waitlist{' '}
                                {(patientCase as any).platoWaitlistDate
                                  ? `· ${format(parseISO((patientCase as any).platoWaitlistDate), 'MMM d')}`
                                  : ''}
                              </Badge>
                            )}
                          </div>
                          {finishingSoon.byCaseId.get(patientCase.id) && (
                            <FinishingSoonRowNote
                              row={finishingSoon.byCaseId.get(patientCase.id)!}
                              disabled={finishingSoon.isKeepingOpen}
                              onKeepOpen={() => finishingSoon.keepOpen(patientCase.id)}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <ReferredBackIndicator
                            referredBackFlag={patientCase.referredBackFlag}
                            referredBackDate={patientCase.referredBackDate}
                            status={patientCase.status}
                            disabled={isFlippingReferredBack || isUpdating}
                            onToggle={(nextFlag) =>
                              handleToggleReferredBack(patientCase, nextFlag)
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-slate-800">{patientCase.nextAction}</p>
                          <p className="text-xs text-slate-500">
                            {getDaysUntilDue(patientCase.nextActionDueDate) ?? 'No due date'}
                          </p>
                        </TableCell>
                        <TableCell className="text-sm text-slate-700">
                          {patientCase.assignedOwnerName ?? 'Unassigned'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/patients/${patientCase.id}`)}
                            >
                              Open
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditCase(patientCase)}
                            >
                              Edit
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* (Removed 2026-07-03: the "Priority queue" sidebar was a THIRD
                rendering of the same cases - the Operations board already pins
                action-needed cases - and "How this board helps" was static
                filler. Case list = full-width search/archive now.) */}
          </div>
        </TabsContent>

        <TabsContent value="board">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle>Operations board</CardTitle>
              <CardDescription>
                Action-needed cases pin to the top. Closed cases collapse below so the active queue
                stays on one screen.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {(() => {
                const actionCases = filteredCases.filter(
                  (patientCase) => getStatusBand(patientCase.status) === 'action'
                );
                const progressCases = filteredCases.filter(
                  (patientCase) => getStatusBand(patientCase.status) === 'progress'
                );
                const closedCases = filteredCases.filter(
                  (patientCase) => getStatusBand(patientCase.status) === 'closed'
                );

                const renderCompactCard = (patientCase: PatientCaseUiRecord) => {
                  const dueLabel = getDaysUntilDue(patientCase.nextActionDueDate);
                  const ownerLabel = patientCase.assignedOwnerName ?? 'Unassigned';

                  return (
                    <button
                      key={patientCase.id}
                      type="button"
                      onClick={() => navigate(`/patients/${patientCase.id}`)}
                      title={
                        patientCase.nextAction
                          ? `${patientCase.nextAction}${dueLabel ? ` (${dueLabel})` : ''} - ${ownerLabel}`
                          : ownerLabel
                      }
                      className="group w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-left transition hover:border-slate-300 hover:bg-slate-50"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate text-sm font-semibold text-slate-900">
                          {patientCase.patientDisplayName}
                        </p>
                        <div className="flex items-center gap-1">
                          {isManualReferralCase(patientCase) && <ManualReferralBadge compact />}
                          {(patientCase as any).platoWaitlistPushedAt && (
                            <Badge
                              variant="outline"
                              className="border-emerald-300 bg-emerald-50 text-emerald-800 whitespace-nowrap px-1.5 py-0 text-[10px] font-medium"
                            >
                              WL{' '}
                              {(patientCase as any).platoWaitlistDate
                                ? format(parseISO((patientCase as any).platoWaitlistDate), 'MMM d')
                                : ''}
                            </Badge>
                          )}
                          <Badge
                            variant="outline"
                            className={cn(
                              'border whitespace-nowrap px-1.5 py-0 text-[10px] font-medium uppercase tracking-wide',
                              STATUS_META[patientCase.status].badgeClassName
                            )}
                          >
                            {getStatusColumnLabel(patientCase.status)}
                          </Badge>
                        </div>
                      </div>
                      <div className="mt-1 flex items-center justify-between gap-2 text-xs text-slate-500">
                        <span className="truncate">
                          {dueLabel ? `Due ${dueLabel}` : 'No due date'}
                        </span>
                        <span className="truncate text-slate-600">{ownerLabel}</span>
                      </div>
                      {patientCase.nextAction && (
                        <p className="mt-1 line-clamp-1 text-xs text-slate-600 group-hover:line-clamp-none">
                          {patientCase.nextAction}
                        </p>
                      )}
                    </button>
                  );
                };

                const renderBand = (
                  title: string,
                  description: string,
                  bandCases: PatientCaseUiRecord[],
                  accentClassName: string
                ) => (
                  <div>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className={cn('h-2 w-2 rounded-full', accentClassName)} />
                        <p className="text-sm font-semibold text-slate-900">{title}</p>
                        <span className="text-xs text-slate-500">
                          {bandCases.length} {bandCases.length === 1 ? 'case' : 'cases'}
                        </span>
                      </div>
                      <p className="hidden text-xs text-slate-500 sm:block">{description}</p>
                    </div>
                    {bandCases.length === 0 ? (
                      <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50/60 px-3 py-2 text-xs text-slate-500">
                        Nothing here right now — new referrals appear automatically from the website
                        and dentist portal.
                      </p>
                    ) : (
                      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {bandCases.map(renderCompactCard)}
                      </div>
                    )}
                  </div>
                );

                return (
                  <>
                    {renderBand(
                      'Action needed',
                      'Fresh referrals and pre-treatment cases waiting on the team.',
                      actionCases,
                      'bg-amber-500'
                    )}

                    <Separator />

                    {renderBand(
                      'In progress',
                      'Treatment accepted or underway.',
                      progressCases,
                      'bg-sky-500'
                    )}

                    <Separator />

                    <div>
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full bg-slate-400" />
                          <p className="text-sm font-semibold text-slate-900">Closed</p>
                          <span className="text-xs text-slate-500">
                            {closedCases.length} {closedCases.length === 1 ? 'case' : 'cases'}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs text-slate-600 hover:text-slate-900"
                          onClick={() => setShowClosedBoardBand((current) => !current)}
                          disabled={closedCases.length === 0}
                        >
                          {showClosedBoardBand ? (
                            <ChevronDown className="mr-1 h-3.5 w-3.5" />
                          ) : (
                            <ChevronRight className="mr-1 h-3.5 w-3.5" />
                          )}
                          {showClosedBoardBand
                            ? 'Hide'
                            : `Show ${closedCases.length} closed`}
                        </Button>
                      </div>
                      {showClosedBoardBand && closedCases.length > 0 && (
                        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                          {closedCases.map(renderCompactCard)}
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {[
              {
                label: 'Total referrals',
                value: analyticsSummary.total,
                cases: analyticsSummary.lists.total,
              },
              {
                label: 'Converted',
                value: analyticsSummary.converted,
                cases: analyticsSummary.lists.converted,
              },
              {
                label: 'Not converted / inactive',
                value: analyticsSummary.notConverted,
                cases: analyticsSummary.lists.notConverted,
              },
              // Conversion rate is a percentage, not a set of patients, so it
              // stays a plain card - there is no "who" behind a ratio.
              { label: 'Conversion rate', value: `${analyticsSummary.conversionRate}%` },
            ].map((metric) => {
              const drillable = Array.isArray(metric.cases) && metric.cases.length > 0;
              return (
                <Card
                  key={metric.label}
                  className={cn(
                    'border-slate-200 shadow-sm',
                    drillable &&
                      'cursor-pointer transition hover:border-primary/60 hover:shadow-md',
                  )}
                  onClick={
                    drillable
                      ? () =>
                          setStatsDrilldown({
                            label: metric.label,
                            cases: metric.cases,
                          })
                      : undefined
                  }
                  role={drillable ? 'button' : undefined}
                  tabIndex={drillable ? 0 : undefined}
                  onKeyDown={
                    drillable
                      ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setStatsDrilldown({
                              label: metric.label,
                              cases: metric.cases,
                            });
                          }
                        }
                      : undefined
                  }
                >
                  <CardContent className="space-y-2 p-5">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                      {metric.label}
                    </p>
                    <p className="text-3xl font-semibold tracking-tight text-slate-950">
                      {metric.value}
                    </p>
                    {drillable && (
                      <p className="text-xs font-medium text-primary">Click to see who</p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle>Referral loop outcomes</CardTitle>
              <CardDescription>
                Track cases that need service recovery, handback follow-up, or referrer explanation.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 md:grid-cols-3">
              {[
                {
                  label: 'No show',
                  value: analyticsSummary.noShow,
                  cases: analyticsSummary.lists.noShow,
                  detail: 'Patient missed the appointment; keep callback/action open.',
                },
                {
                  label: 'Non-compliant / not returning',
                  value: analyticsSummary.nonCompliant,
                  cases: analyticsSummary.lists.nonCompliant,
                  detail: 'Patient did not return to the referring clinic after the loop.',
                },
                {
                  label: 'Referred back',
                  value: analyticsSummary.referredBack,
                  cases: analyticsSummary.lists.referredBack,
                  detail: 'Handback completed and visible for referral relationship review.',
                },
              ].map((metric) => {
                const drillable = metric.cases.length > 0;
                return (
                  <button
                    key={metric.label}
                    type="button"
                    disabled={!drillable}
                    onClick={() =>
                      setStatsDrilldown({ label: metric.label, cases: metric.cases })
                    }
                    className={cn(
                      'rounded-2xl border border-slate-200 bg-white p-4 text-left transition',
                      drillable
                        ? 'cursor-pointer hover:border-primary/60 hover:shadow-md'
                        : 'cursor-default',
                    )}
                  >
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                      {metric.label}
                    </p>
                    <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">
                      {metric.value}
                    </p>
                    <p className="mt-2 text-sm text-slate-600">{metric.detail}</p>
                    {drillable && (
                      <p className="mt-2 text-xs font-medium text-primary">Click to see who</p>
                    )}
                  </button>
                );
              })}
            </CardContent>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle>Referrals by dentist</CardTitle>
                <CardDescription>
                  Volume and conversion rate by referral source in the current clinic view.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {analyticsSummary.byDoctor.map((row) => (
                  <div
                    key={row.doctor}
                    className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-4"
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{row.doctor}</p>
                      <p className="text-xs text-slate-500">
                        {row.converted} converted of {row.total}
                      </p>
                    </div>
                    <Badge variant="outline" className="border-slate-300">
                      {row.rate}%
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle>Referrals by clinic</CardTitle>
                <CardDescription>
                  Company/clinic-wise view for monthly, 3-month, and 6-month review.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {analyticsSummary.byClinic.map((row) => (
                  <div
                    key={row.clinic}
                    className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-4"
                  >
                    <p className="text-sm font-semibold text-slate-900">{row.clinic}</p>
                    <Badge variant="outline" className="border-slate-300">
                      {row.totalCases} cases
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle>Monthly trend</CardTitle>
                <CardDescription>
                  Referral volume by month for the selected filters.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {analyticsSummary.byMonth.map((row) => (
                  <div
                    key={row.month}
                    className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-4"
                  >
                    <p className="text-sm font-semibold text-slate-900">{row.month}</p>
                    <Badge variant="outline" className="border-slate-300">
                      {row.totalCases} cases
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle>PR follow-up alerts</CardTitle>
                <CardDescription>
                  Dentists whose recent referrals dropped 40% or more vs their 6-month average.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {analyticsSummary.dropOffAlerts.length === 0 ? (
                  <p className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                    No referral drop-off alerts in the selected view.
                  </p>
                ) : (
                  analyticsSummary.dropOffAlerts.map((row) => (
                    <div
                      key={row.doctor}
                      className="rounded-2xl border border-amber-200 bg-amber-50 p-4"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <p className="text-sm font-semibold text-amber-950">{row.doctor}</p>
                        <Badge variant="outline" className="border-amber-300 text-amber-900">
                          down {row.declinePct}%
                        </Badge>
                      </div>
                      <p className="mt-2 text-sm text-amber-900">
                        6-month avg: {row.avgPerMonth}/mo. Recent: {row.recentPerMonth}/mo.
                      </p>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>


        <TabsContent value="reconciliation" className="space-y-4">
          <ReferralReconciliation />
        </TabsContent>

        <TabsContent value="referring-dentists" className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Referring dentists</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              External dentists who refer patients to Twin City. Manage their
              contact details and portal logins.
            </p>
          </div>
          <ReferringDentistsTable variant="embedded" />
        </TabsContent>

        <TabsContent value="referral-caselist" className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Referral case list</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Every patient referred to Twin City (from Plato's Referred-By), with
              their treatment stage and status. Click a patient for the full detail.
            </p>
          </div>
          <ReferralCaselist />
        </TabsContent>
      </Tabs>

      {/* Stat-card drill-down: who is behind the number (Samuel 2026-09-10). */}
      <Dialog
        open={statsDrilldown !== null}
        onOpenChange={(open) => !open && setStatsDrilldown(null)}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {statsDrilldown?.label} ({statsDrilldown?.cases.length ?? 0})
            </DialogTitle>
            <DialogDescription>
              These are the cases counted by that number, using the filters currently
              set on this page.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Patient</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden sm:table-cell">Referred by</TableHead>
                  <TableHead className="hidden md:table-cell">Owner</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(statsDrilldown?.cases ?? []).map((patientCase) => (
                  <TableRow
                    key={patientCase.id}
                    className="cursor-pointer"
                    onClick={() => {
                      setStatsDrilldown(null);
                      navigate(`/patients/${patientCase.id}`);
                    }}
                  >
                    <TableCell>
                      <p className="text-sm font-medium text-slate-900">
                        {patientCase.patientDisplayName}
                      </p>
                      {patientCase.platoPatientNumber && (
                        <p className="text-xs text-slate-500">
                          ID {patientCase.platoPatientNumber}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn(
                          'border',
                          STATUS_META[patientCase.status].badgeClassName,
                        )}
                      >
                        {getStatusBadgeLabel(patientCase.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-sm text-slate-600">
                      {patientCase.referringDoctor || '—'}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-slate-600">
                      {patientCase.assignedOwnerName ?? 'Unassigned'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      <PatientCaseModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingCase(null);
        }}
        onSave={handleSaveCase}
        onDelete={handleDeleteCase}
        isDeleting={isDeleting}
        owners={assignableOwnerOptions}
        clinicName={currentClinicName}
        patientCase={editingCase}
      />
    </div>
  );
};

export default PatientsView;
